import { call, put, select } from 'redux-saga/effects';

import isUnleashFeatureEnabled from 'mf-shared/selectors/isUnleashFeatureEnabled';
import * as action from '../../actions';
import * as actNotif from '../../actions/notifications';

import * as api from '../../api/sales';
import { assertErrorHasMessage } from '../../utils/assert';

/**
 * Fetch sales summary from the server
 * @param {object} params
 * @returns {*} sales from server, or false if the request fails
 */
/* Sales Summary Optimize Version */
export function* postSalesSummary(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postSalesSummary, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* getSalesType(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.getSalesType, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch sales summary from the server
 * @param {object} params
 * @returns {*} sales from server, or false if the request fails
 */
/* Gross Profit Optimize Version */
export function* postGrossProfit(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postGrossProfit, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch payment methods from the server
 * @param {object} params
 * @returns {*} payment methods from server, or false if the request fails
 */
export function* getPaymentMethods(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postPaymentMethods, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch item sales from the server
 * @param {object} params
 * @returns {*} item sales from server, or false if the request fails
 */
/* Item Sales Optimize Version */
export function* postItemSales(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postItemSales, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* postItemBundleSales(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postBundleItemSales, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/* Category Sales Optimize Version */
export function* postCategorySales(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postCategorySales, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

export function* postBrandSales(params, outletIds) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.postBrandSales, params, outletIds);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch modifier sales from the server
 * @param {object} params
 * @returns {*} modifier sales from server, or false if the request fails
 */
export function* getModifierSales(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.getModifierSales, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch discounts from the server
 * @param {object} params
 * @returns {*} discounts from server, or false if the request fails
 */
export function* getDiscounts(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const isCustomDiscountReport = yield select(
      isUnleashFeatureEnabled,
      'custom-discount-backoffice-report'
    );
    const response = yield isCustomDiscountReport
      ? call(api.getCustomDiscounts, params)
      : call(api.getDiscounts, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch taxes from the server
 * @param {object} params
 * @returns {*} taxes from server, or false if the request fails
 */
export function* getTaxes(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.getTaxes, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch gratuity from the server
 * @param {object} params
 * @returns {*} gratuities from server, or false if the request fails
 */
export function* getGratuities(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.getGratuities, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch staffs from the server
 * @param {object} params
 * @returns {*} staffs from server, or false if the request fails
 */
export function* getStaffs(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.getStaffs, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}

/**
 * Fetch servers from the server
 * @param {object} params
 * @returns {*} servers from server, or false if the request fails
 */
export function* getServers(params) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  try {
    const response = yield call(api.getServers, params);
    return response;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(action.hideProgressBar());
    yield put(action.sendingRequest(false));
  }
}
